/**
 * Check a map payload against the map document v3 contract before anything
 * draws it.
 *
 * The engine (build_map tool) sends a v3 document: `{ version: 3, title,
 * caption, viewport, layers: [...] }`, where each layer carries its resolved
 * style, legend, and popup spec (engine docs/map-document-v3-contract.md).
 * The client draws exactly what the document says, so anything that does not
 * match the contract is skipped rather than guessed at, and every skip is
 * reported as a warning naming the layer and the reason (#1190).
 *
 * normalizeMapDocument() is a pure function: it returns the warnings and
 * MapPayload logs them. It never throws for malformed DATA (wrong types,
 * missing fields); a genuinely unexpected exception is left to propagate to
 * the map's error boundary.
 */
import isSafeUrl from '@/shared/utils/urlHelpers';

import {
    bandColor,
    featureValue,
    markerStyle,
    polygonStyle,
    styleParts,
} from './mapStyle';

export const MAP_DOCUMENT_VERSION = 3;

/** Layer types the client draws on the map, plus link-out embeds. */
const MAP_RENDER_TYPES = new Set(['choropleth', 'points', 'proportional']);
const EMBED_RENDER_TYPE = 'embed';

/** Real v3 render types that a later ticket draws (#1123). */
const NOT_YET_SUPPORTED = new Set(['lines', 'routes']);

const STYLE_KINDS = new Set(['sequential', 'categorical', 'single']);

/**
 * Colors are applied straight from the document, so only plain hex colors
 * are accepted: anything else (named colors, `url(...)`, injected CSS) skips
 * the layer.
 */
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * TRANSITIONAL(v1): the one color used to draw a v1 payload. A v1 payload has
 * no resolved style and the client may not compute one, so it is drawn as a
 * single-color layer. Bootstrap's primary blue, matching the engine's
 * single-style marker color. Delete with fromV1().
 */
const V1_COLOR = '#0d6efd';

const isObject = (value) =>
    value !== null && typeof value === 'object' && !Array.isArray(value);
const isNonEmptyString = (value) =>
    typeof value === 'string' && value.length > 0;
const isFiniteNumber = (value) =>
    typeof value === 'number' && Number.isFinite(value);
const isColor = (value) => typeof value === 'string' && HEX_COLOR.test(value);

function preview(value) {
    const text = JSON.stringify(value) ?? String(value);
    return text.length > 40 ? `${text.slice(0, 40)}...` : text;
}

/** A marker radius: drawn as a circle's size, so it must be above zero. */
const isRadius = (value) => isFiniteNumber(value) && value > 0;

/**
 * Returns a reason string if a marker's size is unusable, otherwise null.
 * `allowRange` is true only for sequential (proportional) markers, which may
 * send min_radius/max_radius instead of one flat `radius`; when they do, both
 * must be valid radii with min_radius <= max_radius, or the circles would be
 * invisible or sized backwards.
 */
function radiusProblem(parts, renderType, allowRange) {
    const sendsRange =
        parts.min_radius !== undefined || parts.max_radius !== undefined;
    if (allowRange && sendsRange) {
        const { min_radius: min, max_radius: max } = parts;
        return isRadius(min) && isRadius(max) && min <= max
            ? null
            : `${renderType} style has an invalid marker radius range ${preview(
                  [min, max]
              )}`;
    }
    if (parts.radius === undefined) {
        return `${renderType} style has no marker radius`;
    }
    return isRadius(parts.radius)
        ? null
        : `${renderType} style has an invalid marker radius ${preview(
              parts.radius
          )}`;
}

/** Every color a style mentions, so one check covers all of them. */
function styleColors(style) {
    const marker = isObject(style.marker) ? style.marker : {};
    const colors = [];
    [style.ramp, marker.ramp].forEach((ramp) => {
        if (Array.isArray(ramp)) colors.push(...ramp);
    });
    [style.classes, marker.classes].forEach((classes) => {
        if (Array.isArray(classes)) {
            // Malformed (non-object) classes are reported by the
            // class check below, not as a bad color.
            classes.forEach((c) => {
                if (isObject(c)) colors.push(c.color);
            });
        }
    });
    if ('color' in marker) colors.push(marker.color);
    if (isObject(style.no_data)) colors.push(style.no_data.fill);
    if (isObject(style.stroke) && 'color' in style.stroke) {
        colors.push(style.stroke.color);
    }
    return colors;
}

/**
 * A sequential style's band boundaries: N+1 finite, non-decreasing numbers
 * for N ramp colors (contract: "ramp[i] draws values in [breaks[i],
 * breaks[i+1])"). A degenerate layer (every value identical) is the N = 1
 * case, a one-color ramp with breaks [v, v], so it passes too.
 */
function isValidBreaks(breaks, rampLength) {
    return (
        Array.isArray(breaks) &&
        breaks.length === rampLength + 1 &&
        breaks.every(isFiniteNumber) &&
        breaks.every((b, i) => i === 0 || breaks[i - 1] <= b)
    );
}

/** Returns a reason string if the style is unusable, otherwise null. */
function styleProblem(style, renderType) {
    if (!isObject(style)) {
        return 'style is missing';
    }
    if (!STYLE_KINDS.has(style.kind)) {
        return `unknown style.kind ${preview(style.kind)}`;
    }
    const usesMarker = renderType !== 'choropleth';
    if (usesMarker && !isObject(style.marker)) {
        return `${renderType} style has no marker`;
    }
    const badColor = styleColors(style).find((color) => !isColor(color));
    if (badColor !== undefined) {
        return `invalid color ${preview(badColor)}`;
    }
    if (style.no_data !== undefined && !isObject(style.no_data)) {
        return 'style.no_data is malformed';
    }

    const parts = styleParts(style, renderType);

    if (style.kind === 'sequential') {
        if (!isNonEmptyString(style.value_field)) {
            return 'sequential style has no value_field';
        }
        const { domain } = style;
        if (
            !Array.isArray(domain) ||
            domain.length !== 2 ||
            !domain.every(isFiniteNumber)
        ) {
            return `sequential style has an invalid domain ${preview(domain)}`;
        }
        if (!Array.isArray(parts.ramp) || parts.ramp.length === 0) {
            return 'sequential style has no ramp';
        }
        if (!isValidBreaks(parts.breaks, parts.ramp.length)) {
            return `sequential style breaks do not match ramp ${preview(
                parts.breaks
            )}`;
        }
        return usesMarker ? radiusProblem(parts, renderType, true) : null;
    }

    if (style.kind === 'categorical') {
        if (!isNonEmptyString(style.value_field)) {
            return 'categorical style has no value_field';
        }
        if (!Array.isArray(parts.classes) || parts.classes.length === 0) {
            return 'categorical style has no classes';
        }
        const malformed = parts.classes.some(
            (c) =>
                !isObject(c) ||
                typeof c.value !== 'string' ||
                typeof c.label !== 'string'
        );
        if (malformed) {
            return 'categorical style has a malformed class';
        }
        return usesMarker ? radiusProblem(parts, renderType, false) : null;
    }

    // single
    if (usesMarker) {
        if (!isColor(style.marker.color)) {
            return 'single style has no color';
        }
        return radiusProblem(parts, renderType, false);
    }
    if (!Array.isArray(style.ramp) || !isColor(style.ramp[0])) {
        return 'single style has no color';
    }
    return null;
}

function legendProblem(legend) {
    if (!isObject(legend)) {
        return 'legend is missing';
    }
    if (!Array.isArray(legend.entries)) {
        return 'legend has no entries array';
    }
    const malformed = legend.entries.some(
        (entry) => !isObject(entry) || typeof entry.label !== 'string'
    );
    if (malformed) {
        return 'legend has a malformed entry';
    }
    // Entry colors become swatch fills (MapLegend), so they get the same
    // hex-only check as the style's colors. Entries without a swatch (e.g. a
    // single style's "No field available to style by") have no color at all.
    const badEntry = legend.entries.find(
        (entry) =>
            entry.color !== undefined &&
            entry.color !== null &&
            !isColor(entry.color)
    );
    if (badEntry) {
        return `invalid legend color ${preview(badEntry.color)}`;
    }
    // A proportional legend entry's radius becomes an SVG circle's size.
    const badRadius = legend.entries.find(
        (entry) =>
            entry.radius !== undefined &&
            entry.radius !== null &&
            !(isFiniteNumber(entry.radius) && entry.radius > 0)
    );
    return badRadius
        ? `invalid legend radius ${preview(badRadius.radius)}`
        : null;
}

/**
 * A v3 popup must name its title_field. `untitledAllowed` is only ever true
 * for a TRANSITIONAL(v1) layer, whose adapted popup has `title_field: null`
 * (v1 never had a title field); see v1Popup().
 */
function isValidPopup(popup, untitledAllowed) {
    const titleOk =
        isNonEmptyString(popup?.title_field) ||
        (untitledAllowed && popup?.title_field === null);
    return (
        isObject(popup) &&
        titleOk &&
        Array.isArray(popup.fields) &&
        popup.fields.every(
            (field) =>
                isObject(field) &&
                isNonEmptyString(field.key) &&
                typeof field.label === 'string'
        )
    );
}

/** A GeoJSON position: `[lng, lat]` (an optional altitude is ignored). */
const isPosition = (value) =>
    Array.isArray(value) &&
    value.length >= 2 &&
    isFiniteNumber(value[0]) &&
    isFiniteNumber(value[1]);

/** A Polygon's coordinates: one or more rings, each a list of positions. */
const isPolygonRings = (rings) =>
    Array.isArray(rings) &&
    rings.length > 0 &&
    rings.every(
        (ring) =>
            Array.isArray(ring) && ring.length > 0 && ring.every(isPosition)
    );

function hasDrawableGeometry(feature, renderType) {
    if (!isObject(feature) || !isObject(feature.geometry)) {
        return false;
    }
    const { type, coordinates } = feature.geometry;
    if (renderType === 'choropleth') {
        // Checked down to every position: BiliMap's geojsonToPaths assumes
        // the nesting is right and throws on a malformed polygon, which would
        // take down every layer on the map instead of skipping one feature.
        if (type === 'Polygon') {
            return isPolygonRings(coordinates);
        }
        return (
            type === 'MultiPolygon' &&
            Array.isArray(coordinates) &&
            coordinates.length > 0 &&
            coordinates.every(isPolygonRings)
        );
    }
    return type === 'Point' && isPosition(coordinates);
}

/**
 * Validate one v3 layer. Returns `{ layer, warnings }` for a usable layer
 * (features filtered, popup dropped if invalid) or `{ skip: warning }`.
 * `isV1` is true only for the one layer fromV1() builds (TRANSITIONAL(v1)).
 */
function checkLayer(raw, index, isV1 = false) {
    if (!isObject(raw)) {
        return {
            skip: {
                layer_id: null,
                label: null,
                reason: `layers[${index}] is not an object`,
            },
        };
    }
    const label = typeof raw.label === 'string' ? raw.label : null;
    const id = isNonEmptyString(raw.id) ? raw.id : null;
    // The data credit shown under the map. Optional: a layer without one
    // still draws, it just has no "Source:" line.
    const attribution = isNonEmptyString(raw.attribution)
        ? raw.attribution
        : null;
    const skip = (reason) => ({ skip: { layer_id: id, label, reason } });

    if (!id) return skip('missing id');
    if (label === null) return skip('missing label');
    if (!isFiniteNumber(raw.z)) return skip('missing z (draw order)');

    if (raw.render_type === EMBED_RENDER_TYPE) {
        const hasUrl = isNonEmptyString(raw.fallback_url);
        if (!hasUrl && !isNonEmptyString(raw.embed_code)) {
            return skip('embed layer has no fallback_url or embed_code');
        }
        // fallback_url becomes a link the reader clicks (EmbeddedMapFrame),
        // so only http(s) URLs are accepted: a `javascript:` URL would run
        // script on click. The iframe src is checked separately, by
        // EmbeddedMapFrame's extractSafeEmbedSrc().
        if (hasUrl && !isSafeUrl(raw.fallback_url)) {
            return skip(`unsafe fallback_url ${preview(raw.fallback_url)}`);
        }
        return {
            layer: {
                id,
                label,
                z: raw.z,
                render_type: EMBED_RENDER_TYPE,
                attribution,
                embed_code: isNonEmptyString(raw.embed_code)
                    ? raw.embed_code
                    : null,
                fallback_url: hasUrl ? raw.fallback_url : null,
                // Tri-state: true, false, or null (never checked).
                embeddable:
                    typeof raw.embeddable === 'boolean' ? raw.embeddable : null,
            },
            warnings: [],
        };
    }

    if (NOT_YET_SUPPORTED.has(raw.render_type)) {
        return skip(
            `render_type ${preview(raw.render_type)} is not supported yet (#1123)`
        );
    }
    if (!MAP_RENDER_TYPES.has(raw.render_type)) {
        return skip(`unknown render_type ${preview(raw.render_type)}`);
    }

    const { geometry } = raw;
    if (
        !isObject(geometry) ||
        geometry.type !== 'FeatureCollection' ||
        !Array.isArray(geometry.features)
    ) {
        return skip('geometry is not a FeatureCollection');
    }

    const styleReason = styleProblem(raw.style, raw.render_type);
    if (styleReason) return skip(styleReason);
    const legendReason = legendProblem(raw.legend);
    if (legendReason) return skip(legendReason);

    const warnings = [];
    const warn = (reason) => warnings.push({ layer_id: id, label, reason });

    // Feature-level checks: drop what can't be drawn, report it once. Each
    // kept feature's draw style is resolved here, once, and handed to BiliMap
    // as `drawables`, so color is decided in exactly one place.
    const resolve =
        raw.render_type === 'choropleth' ? polygonStyle : markerStyle;
    let badGeometry = 0;
    let noColor = 0;
    let outOfBand = 0;
    const features = [];
    const drawables = [];
    geometry.features.forEach((feature) => {
        if (!hasDrawableGeometry(feature, raw.render_type)) {
            badGeometry += 1;
            return;
        }
        const usable = isObject(feature.properties)
            ? feature
            : { ...feature, properties: {} };
        const style = resolve(raw, usable);
        if (!style) {
            noColor += 1;
            return;
        }
        if (isOutOfBand(raw, usable)) {
            outOfBand += 1;
        }
        features.push(usable);
        drawables.push({ feature: usable, style });
    });
    if (raw.render_type === 'proportional') {
        // Largest circles first, so smaller ones draw on top of them and stay
        // visible and clickable where they overlap. (Stable sort: equal radii
        // keep document order.)
        drawables.sort((a, b) => b.style.radius - a.style.radius);
    }
    if (badGeometry) {
        warn(
            `${badGeometry} feature(s) skipped: geometry does not match ` +
                `render_type ${preview(raw.render_type)}`
        );
    }
    if (noColor) {
        // A contract guard, not an expected path: the engine adds `no_data`
        // to any style whose fetched features have a missing or unmatched
        // value, so a feature with no class/band color and no no_data fill
        // means the document broke the contract. Those features are not
        // drawn (the client has no color it may use), and the warning makes
        // the engine-side regression visible.
        warn(
            `${noColor} feature(s) not drawn: no color in the style for their ` +
                `value and no no_data fill`
        );
    }
    if (outOfBand) {
        // Still drawn, in the no_data style, so the map stays complete. But
        // a number that no band contains is not the same as a missing value:
        // the engine's breaks span the whole domain, so this only happens
        // through an engine bug (like #222's rounded breaks[0]), and the
        // warning keeps it from passing as "No data" silently.
        warn(
            `${outOfBand} feature(s) have a value outside the style's ` +
                `breaks; drawn as no_data`
        );
    }
    // The engine never sends an empty layer (a fetch with no features is
    // left out of the document, build_map's "returned no mappable data"), so
    // an empty FeatureCollection breaks the contract like any other bad
    // layer: skipped and reported, not drawn as an empty map with a legend.
    if (geometry.features.length === 0) {
        return skip('layer has no features');
    }
    if (features.length === 0) {
        return skip('no features with drawable geometry');
    }

    let popup = null;
    if (raw.popup !== undefined && raw.popup !== null) {
        if (isValidPopup(raw.popup, isV1)) {
            popup = raw.popup;
        } else {
            warn('invalid popup spec; popups disabled for this layer');
        }
    }

    return {
        layer: {
            id,
            label,
            z: raw.z,
            render_type: raw.render_type,
            geometry: { ...geometry, features },
            drawables,
            feature_count: isFiniteNumber(raw.feature_count)
                ? raw.feature_count
                : features.length,
            style: raw.style,
            legend: raw.legend,
            popup,
            attribution,
            truncated: raw.truncated === true,
            note: typeof raw.note === 'string' ? raw.note : null,
        },
        warnings,
    };
}

/**
 * True when a sequential layer's feature has a real number that falls in none
 * of the style's bands, so mapStyle.js drew it with the no_data fallback.
 */
function isOutOfBand(layer, feature) {
    if (layer.style.kind !== 'sequential') {
        return false;
    }
    const value = featureValue(layer, feature);
    if (!isFiniteNumber(value)) {
        return false;
    }
    const { breaks, ramp } = styleParts(layer.style, layer.render_type);
    return bandColor(value, breaks, ramp) === null;
}

/**
 * TRANSITIONAL(v1): adapt a v1 (single-dataset, hub #1189 era) payload into a
 * one-layer v3-shaped document.
 *
 * WHY: #1190 requires v1 payloads to keep rendering "during transition". A v1
 * payload has a value_field/value_domain but no resolved colors, and the
 * client must not compute colors, so it is drawn as a single-color layer
 * (V1_COLOR) with a one-entry legend. Its popup lists the clicked feature's
 * properties the way the pre-#1190 popup did (see v1Popup()).
 *
 * DELETE WHEN: no deployed engine sends v1 payloads (engine develop has sent
 * only v3 since #220). Remove fromV1(), v1Popup(), V1_COLOR, the v1 branch in
 * normalizeMapDocument(), and the `isV1`/`untitledAllowed` parameters of
 * checkLayer() and isValidPopup().
 */
export function fromV1(payload) {
    const name = isNonEmptyString(payload.source?.name)
        ? payload.source.name
        : 'Map';
    const base = { id: 'v1', label: name, z: 0 };

    if (payload.render_type === EMBED_RENDER_TYPE) {
        return {
            version: MAP_DOCUMENT_VERSION,
            title: name,
            caption: '',
            layers: [
                {
                    ...base,
                    render_type: EMBED_RENDER_TYPE,
                    embed_code: payload.embed_code,
                    fallback_url: payload.fallback_url,
                    embeddable: payload.embeddable,
                },
            ],
        };
    }

    const style =
        payload.render_type === 'choropleth'
            ? {
                  kind: 'single',
                  ramp: [V1_COLOR],
                  stroke: { color: V1_COLOR, weight: 1 },
              }
            : {
                  kind: 'single',
                  marker: { shape: 'circle', color: V1_COLOR, radius: 6 },
              };
    return {
        version: MAP_DOCUMENT_VERSION,
        title: name,
        caption: '',
        layers: [
            {
                ...base,
                render_type: payload.render_type,
                geometry: payload.geometry,
                feature_count: payload.feature_count,
                truncated: payload.truncated,
                note: payload.note,
                style,
                legend: {
                    title: isNonEmptyString(payload.legend?.title)
                        ? payload.legend.title
                        : null,
                    kind: 'single',
                    entries: [{ label: name, color: V1_COLOR }],
                },
                popup: v1Popup(payload.geometry),
            },
        ],
    };
}

/**
 * TRANSITIONAL(v1): a popup spec for a v1 payload, which never had one.
 *
 * WHY: before #1190, clicking a v1 feature listed every one of its
 * properties, labelled by the raw field name. A v1 map is still live while
 * an older engine is deployed, so its popup keeps that content instead of
 * shrinking to just the layer name.
 *
 * CURRENT RULE: every property name found on any feature, in first-seen
 * order, labelled as-is (raw names like "Hsn__", exactly as before; v1 has
 * no humanized labels and the client does not invent them). `title_field`
 * is null because v1 has no title field, so the header shows the layer name
 * alone. null (no popup) when no feature has any properties.
 *
 * DELETE WHEN: fromV1() is deleted.
 */
function v1Popup(geometry) {
    const features = Array.isArray(geometry?.features) ? geometry.features : [];
    const keys = new Set();
    features.forEach((feature) => {
        if (isObject(feature) && isObject(feature.properties)) {
            Object.keys(feature.properties).forEach((key) => {
                if (key) keys.add(key);
            });
        }
    });
    if (keys.size === 0) {
        return null;
    }
    return {
        title_field: null,
        fields: [...keys].map((key) => ({ key, label: key })),
    };
}

function emptyResult(warnings, title = '', caption = '') {
    return {
        title,
        caption,
        mapLayers: [],
        embedLayers: [],
        skippedLayers: [],
        warnings,
    };
}

/**
 * @param {*} payload The `map` object from an engine chat response.
 * @returns {null | {
 *   title: string, caption: string,
 *   mapLayers: object[],   // drawable layers, sorted by z (bottom first);
 *                          // each has `drawables`: [{feature, style}] in
 *                          // draw order, style from utils/mapStyle.js
 *   embedLayers: object[], // link-out layers, in document order
 *   skippedLayers: {layer_id: ?string, label: ?string}[], // whole layers
 *                          // left out, so the reader can be told (feature-
 *                          // level problems are in `warnings` only)
 *   warnings: {scope: 'document'|'layer', layer_id: ?string,
 *              label: ?string, reason: string, render_type?: *, version?: *}[]
 * }} null when there is no payload at all.
 */
export function normalizeMapDocument(payload) {
    if (payload === null || payload === undefined) {
        return null;
    }
    const documentWarning = (reason) => ({
        scope: 'document',
        layer_id: null,
        label: null,
        reason,
        render_type: payload?.render_type,
        version: payload?.version,
    });

    if (!isObject(payload)) {
        return emptyResult([documentWarning('map payload is not an object')]);
    }

    const isV1 =
        payload.version === undefined &&
        typeof payload.render_type === 'string';
    let doc = payload;
    if (isV1) {
        // TRANSITIONAL(v1): see fromV1()
        doc = fromV1(payload);
    } else if (payload.version !== MAP_DOCUMENT_VERSION) {
        return emptyResult([
            documentWarning(
                'payload is neither a v3 map document nor a v1 payload'
            ),
        ]);
    }

    const title = typeof doc.title === 'string' ? doc.title : '';
    const caption = typeof doc.caption === 'string' ? doc.caption : '';
    if (!Array.isArray(doc.layers)) {
        return emptyResult(
            [documentWarning('layers is not an array')],
            title,
            caption
        );
    }

    const warnings = [];
    const mapLayers = [];
    const embedLayers = [];
    const skippedLayers = [];
    const seenIds = new Set();
    const skipLayer = (skip) => {
        warnings.push({ scope: 'layer', ...skip });
        skippedLayers.push({ layer_id: skip.layer_id, label: skip.label });
    };
    doc.layers.forEach((raw, index) => {
        const result = checkLayer(raw, index, isV1);
        if (result.skip) {
            skipLayer(result.skip);
            return;
        }
        const { layer } = result;
        if (seenIds.has(layer.id)) {
            skipLayer({
                layer_id: layer.id,
                label: layer.label,
                reason: `duplicate id ${preview(layer.id)}`,
            });
            return;
        }
        seenIds.add(layer.id);
        result.warnings.forEach((w) => warnings.push({ scope: 'layer', ...w }));
        if (layer.render_type === EMBED_RENDER_TYPE) {
            embedLayers.push(layer);
        } else {
            mapLayers.push(layer);
        }
    });

    // Draw order: lowest z first (area shading under points). Array sort is
    // stable, so equal z keeps document order.
    mapLayers.sort((a, b) => a.z - b.z);
    return { title, caption, mapLayers, embedLayers, skippedLayers, warnings };
}
